import { SwrInfiniteDataType, SwrInfiniteMutatePageType, SwrMutateSiblingsType } from '@type/swr';
import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@store/index';
import { resetApplicantTalentList } from '@store/reducers/employer/lists/listsReducer';
import { getApplicantTalentList } from '@store/reducers/employer/lists/listsSelector';


// Interface
// ************************************
interface UseApplicantListsMutator<DataType> {
  infiniteListData: SwrInfiniteDataType<DataType>,
  swrInfiniteMutatePageFunc: SwrInfiniteMutatePageType<DataType>,
  swrMutateSiblingsFunc?: SwrMutateSiblingsType<DataType>,
  otherMutations?: () => void,
}


/**
 * @description Helper hook for updating the infinite list when in a modal and the CV was unlocked or status
 * (rejected, interview, shortlist, etc.) was changed (ie: via user click). Then, after the modal is closed,
 * we will revalidate the infinite list according to these states so the user will see the changes (status pills, applicant name)
 * reflected immediately.
 *
 * *For updating the Redux state, use the useApplicantListsRedux hook.*
 *
 * - **infiniteListData** - DataType[] - The infinite list data.
 * - **infiniteListMutationFunc** - SwrInfiniteMutatorFunction<DataType[]> - The SWR infinite list mutation function.
 * - **revalidateOtherStatusLists** - boolean - Whether to revalidate other status lists (ie: shortlist, interview, etc.).
 * - **otherMutations** - () => void - Other mutations to run, optional. Make a function that runs other mutations.
 *
 * @example
 * useApplicantListsMutator({
 *  infiniteListData: data,
 *  infiniteListMutationFunc: mutate,
 *  revalidateOtherStatusLists: true,
 * });
 */
export const useApplicantListsMutator = <DataType>(props: UseApplicantListsMutator<DataType>) => {
  // Destructure props
  const {
    infiniteListData,
    swrInfiniteMutatePageFunc,
    swrMutateSiblingsFunc,
    otherMutations,
  } = props;


  // Main Redux states
  // *************************************
  const dispatch = useAppDispatch();
  const listState = useAppSelector(getApplicantTalentList);


  // Update the current infinite list when the Redux state changes. We watch if:
  // - The CV detail modal is open.
  // - The applicant/talent status has changed (interview, shortlist, etc.).
  // - The applicant/talent has been contacted (message, show contact details).
  // We will mutate the infinite list according to these states.
  // ***********************************************
  useEffect(() => {
    if (infiniteListData && listState && listState?.modalOpen === false && (listState?.unlockedSlugs || listState?.statusChangedSlugs)) {
      // Helper to merge the slugs from the two lists
      const mergedSlugs = new Set([...(listState?.unlockedSlugs || []), ...(listState?.statusChangedSlugs || [])]);
      const pagesToUpdate = new Set<number | undefined>();


      // 1. Handle the currently displayed list
      // ***************************************
      // Find the pages that contain the slugs. We will update only the pages that contain the slugs.
      infiniteListData?.forEach((listPage) => {
        const pageItems = listPage?.['items' as keyof typeof listPage] as unknown[] || [];
        const pageHasSlug = pageItems?.some((item) => mergedSlugs.has(item?.['slug' as keyof typeof item] || ''));
        if (pageHasSlug && listPage?.['page' as keyof typeof listPage]) pagesToUpdate.add(listPage?.['page' as keyof typeof listPage] as number);
      });

      // UPDATE ONLY THE REQUIRED PAGES: we're being smart and efficient
      if (pagesToUpdate.size > 0) {
        void swrInfiniteMutatePageFunc((newData: DataType) => pagesToUpdate.has(newData?.['page' as keyof typeof newData] as number));

        // Mutate the siblings if needed
        if (swrMutateSiblingsFunc) void swrMutateSiblingsFunc();

        // Other mutations if needed
        if (otherMutations) otherMutations();
      }


      // 2. Reset the Redux state
      // ***************************************
      dispatch(resetApplicantTalentList());
    }
  }, [
    listState,
    swrInfiniteMutatePageFunc,
    swrMutateSiblingsFunc,
    dispatch,
    infiniteListData,
    otherMutations,
  ]);
};
